// ═══════════════════════════════════════════════════════
// mergeSession — Fusionne une session temporaire dans sa cible
// Appelé quand on clique "Prête" sur une session avec merge_target_id
// ═══════════════════════════════════════════════════════

import { supabase } from './supabase.js';

/**
 * Fusionne la session source dans la session cible.
 * - Ajoute les order_refs/invoice_refs
 * - Déplace les colis, photos, order_lines
 * - Met à jour les compteurs
 * - Supprime la session source
 * 
 * @param {string} sourceId - ID de la session temporaire (celle qu'on vient de préparer)
 * @param {string} targetId - ID de la session cible (prete/depart_valide dans Départs)
 * @returns {object} La session cible mise à jour
 */
export async function mergeSession(sourceId, targetId) {
  // 1. Charger les deux sessions
  const [sourceRes, targetRes] = await Promise.all([
    supabase.from('elixir_prep_sessions').select('*').eq('id', sourceId).single(),
    supabase.from('elixir_prep_sessions').select('*').eq('id', targetId).single(),
  ]);
  const source = sourceRes.data;
  const target = targetRes.data;
  if (!source || !target) throw new Error('Session introuvable');

  // 2. Fusionner les refs
  const mergedRefs = [...new Set([...(target.order_refs || []), ...(source.order_refs || [])])];
  const mergedInvoiceRefs = [...new Set([...(target.invoice_refs || []), ...(source.invoice_refs || [])])];

  // 3. Déplacer les colis
  await supabase.from('elixir_prep_colis')
    .update({ session_id: targetId })
    .eq('session_id', sourceId);

  // 4. Déplacer les photos
  await supabase.from('elixir_prep_photos')
    .update({ session_id: targetId })
    .eq('session_id', sourceId);

  // 5. Déplacer les order_lines
  await supabase.from('elixir_prep_order_lines')
    .update({ session_id: targetId })
    .eq('session_id', sourceId);

  // 6. Déplacer les anomalies
  await supabase.from('elixir_prep_anomalies')
    .update({ session_id: targetId })
    .eq('session_id', sourceId);

  // 7. Déplacer les remarques
  await supabase.from('elixir_prep_remarks')
    .update({ session_id: targetId })
    .eq('session_id', sourceId);

  // 8. Additionner les compteurs colis des deux sessions (pas de elixir_prep_colis)
  const update = {
    order_refs: mergedRefs,
    invoice_refs: mergedInvoiceRefs,
    nb_amb: (target.nb_amb || 0) + (source.nb_amb || 0),
    nb_froid: (target.nb_froid || 0) + (source.nb_froid || 0),
    nb_cartons: (target.nb_cartons || 0) + (source.nb_cartons || 0),
    nb_cartons_vrac: (target.nb_cartons_vrac || 0) + (source.nb_cartons_vrac || 0),
    date_order: target.date_order && source.date_order 
      ? (target.date_order < source.date_order ? target.date_order : source.date_order) 
      : target.date_order || source.date_order,
    date_order_last: target.date_order_last && source.date_order_last
      ? (target.date_order_last > source.date_order_last ? target.date_order_last : source.date_order_last)
      : target.date_order_last || source.date_order_last,
    updated_at: new Date().toISOString(),
  };
  await supabase.from('elixir_prep_sessions').update(update).eq('id', targetId);

  // 10. Supprimer la session source
  await supabase.from('elixir_prep_sessions').delete().eq('id', sourceId);

  console.log('[merge] Session', sourceId, '→', targetId, '— refs:', mergedRefs.length);

  return { ...target, ...update };
}
